import { Tracking } from "Types/api/Tracking";
import { getTrackingAutoNation } from "src/utils/tracking/autoNationTag";
import { getFourEyesId } from "src/utils/tracking/mitsubishiFourEyesTag";
import { getGoogleAnalyticsClientId } from "src/utils/tracking/gaClientId";
import { getCarsIdFromURL } from "src/utils/tracking/getCarsIdFromURL";
import getFacebookTrackingObject from "src/utils/tracking/facebook";
import getRoxanneVisitId from "src/utils/tracking/roxanne";
import { getDeviceType } from "src/utils/tracking/getDeviceType";
import { patchTrackingRequest, setTracking } from "src/store/actions/tracking";
import { getUtmTrackingObject } from "src/utils/tracking/getUtmTrackingObject";
import { getHondaTrackingObject } from "src/utils/tracking/getHondaTrackingObject";
import { getCCDPTrackingObject } from "src/utils/tracking/getCCDPTrackingObject";

// Tracking data from 3rd party scripts that might not be ready
export type AsyncTrackingValues = {
    [P in keyof Tracking]: () => Tracking[P];
};
export const ASYNC_TRACKERS = {
    autonation: getTrackingAutoNation,
    adp_foureyes_id: getFourEyesId,
    clientId: getGoogleAnalyticsClientId,
    roxanne: getRoxanneVisitId,
    facebook: getFacebookTrackingObject,
};

// Tracking data that is always available
export type StaticTrackingValues = Partial<Tracking>;

export const getStaticTrackingValues = (env: string, slug: string): Partial<Tracking> => {
    return {
        carsId: getCarsIdFromURL(),
        device_type: getDeviceType(),
        client_user_agent: navigator.userAgent,
        honda: getHondaTrackingObject(env, slug),
        utm: getUtmTrackingObject(env, slug),
    };
};

export type PromiseTrackingValues = {
    [P in keyof Tracking]: () => Promise<Tracking[P]>;
};
export const PROMISE_TRACKING_VALUES = {
    ccdp: getCCDPTrackingObject,
};

const handleHostTrackingData = async (
    dispatch: (action: { payload?: any; type: string }) => void,
    checkStateValues: () => { uuid: string | undefined; isAppOpen: boolean },
    asyncTrackingValues: AsyncTrackingValues,
    staticTrackingValues: StaticTrackingValues,
    promiseTrackingValues: PromiseTrackingValues
): Promise<void> => {
    // Get [values from loaded 3rd party trackers, trackers to retry]
    const [trackingValues, retryTrackers]: [Partial<Tracking>, Partial<AsyncTrackingValues>] = Object.entries(
        asyncTrackingValues
    ).reduce(
        ([values, retry], [trackingKey, trackingGetter]) => {
            const trackingValue = trackingGetter!();

            if (trackingValue) {
                return [{ ...values, [trackingKey]: trackingValue }, retry];
            }

            return [values, { ...retry, [trackingKey]: trackingGetter }];
        },
        [{}, {}]
    );

    // Retry other keys every 2 seconds, max 5 times each.
    Object.entries(retryTrackers).forEach(([trackingKey, trackingGetter]) => {
        let attempts = 0;
        const intervalID = setInterval(() => {
            const trackingValue = trackingGetter!();

            if (trackingValue) {
                const { uuid, isAppOpen } = checkStateValues();
                if (uuid && isAppOpen) {
                    dispatch(patchTrackingRequest({ [trackingKey]: trackingValue }, uuid));
                } else {
                    dispatch(setTracking({ [trackingKey]: trackingValue }));
                }

                clearInterval(intervalID);
            }

            if (++attempts === 5) {
                clearInterval(intervalID);
            }
        }, 2000);
    });

    const promises = {};
    for (const [trackingKey, trackingGetter] of Object.entries(promiseTrackingValues)) {
        promises[trackingKey] = await trackingGetter();
    }

    const availableTrackers = {
        ...staticTrackingValues,
        ...trackingValues,
        ...promises,
    };

    if (Object.keys(availableTrackers).length === 0) {
        return;
    }

    const { uuid, isAppOpen } = checkStateValues();

    if (uuid && isAppOpen) {
        dispatch(patchTrackingRequest(availableTrackers, uuid));
    } else {
        dispatch(setTracking(availableTrackers));
    }
};

export default handleHostTrackingData;
